import { PlatformLogo } from "@tripcart/traveler-ui";

import type { Translator } from "@/lib/translations";

import { StarRow } from "../_star-row";

import styles from "./rating-summary.module.css";

/**
 * The guide's `.rvs` — the score beside the platforms it is drawn from.
 *
 * This panel RESHAPES around how many review platforms an agency has actually
 * connected, which is the behaviour the prototype's own 1/2/3/4 switcher was
 * demonstrating. Its comment says it plainly: "The panel adapts to how many
 * platforms are actually connected. With one, a 2x2 grid would read as three
 * empty cells, so it collapses to a row."
 *
 * The states, and what changes in each:
 *
 *  - ONE platform ("solo"): the score block drops its dividing rule, the
 *    platform moves to a right-aligned PILL rather than a grid cell, and the
 *    label reads "Verified on" instead of "Also rated on" — because with a
 *    single source there is no "also". The source FILTER is hidden entirely by
 *    the caller for the same reason: one source gives it nothing to filter.
 *  - TWO or THREE: one column each, so the row fills without a gap.
 *  - FOUR: a 2×2 block.
 *
 * What we show per platform is that platform's average SCORE, as the guide
 * does (`RV_PLATS` there carries a `score` per platform, and the label above
 * the grid says "Also rated on").
 *
 * This printed the review COUNT until 2026-09-21, on the reasoning that our
 * payload carried no per-platform average so a score would be invented. The
 * premise was wrong rather than the reasoning: the aggregate query already
 * grouped by platform, it just never selected `avg(rating)` alongside its
 * `count(*)`. Adding that one column is what closed it — the average is as
 * real as the tally was, and it is the figure the label promises.
 */

/**
 * Each platform's display name and disc. THE one place either is defined — the
 * source filter imports this rather than keeping its own copy, which is how the
 * two ended up disagreeing: the summary knew "TripAdvisor" while the filter
 * chip beside it read "tripadvisor".
 *
 * The MARK itself is now the platform's real logo (`PlatformLogo` from
 * `@tripcart/traveler-ui`) — the coloured disc below is only
 * the fallback for a platform we have no logo for, including Tripcart's own
 * reviews. Discs alone were the bug: Trustpilot and TripAdvisor both rendered a
 * green "T", so the one thing the mark exists to say — which site this came
 * from — was the one thing it could not.
 *
 * Colour is the platform's own. These deliberately do NOT follow the tenant's
 * brand — a recognisable mark is the entire point, and a Google disc in an
 * operator's green would not be one.
 *
 * Keyed by whatever the reviews aggregate sends. That set is WIDER than
 * `ReviewDto.source` (which is `platform | google | tripadvisor`): the live
 * payload also carries `trustpilot` and `facebook`, because the per-review
 * enum constrains what a review row can say about itself, not which platforms
 * an agency has connected. An unknown key still renders — see the fallback in
 * `PlatformMark` — so a new integration degrades to a grey initial rather than
 * vanishing from the panel.
 */
const PLATFORM_MARKS: Record<
  string,
  {
    label: string;
    initial: string;
    /** The disc's fill — a `data-tone` rule in `rating-summary.module.css`. */
    tone: "primary" | "info-deep" | "success-deep" | "info";
  }
> = {
  platform: { label: "Tripcart", initial: "T", tone: "primary" },
  google: { label: "Google", initial: "G", tone: "info-deep" },
  tripadvisor: { label: "TripAdvisor", initial: "T", tone: "success-deep" },
  trustpilot: { label: "Trustpilot", initial: "T", tone: "primary" },
  facebook: { label: "Facebook", initial: "F", tone: "info" },
};

/** The platform's display name, or the raw key when we do not know it. */
export function platformLabel(platform: string): string {
  return PLATFORM_MARKS[platform]?.label ?? platform;
}

/**
 * The order review sources are shown in, everywhere.
 *
 * Fixed rather than however the payload happened to arrive: the score grid and
 * the review rails below it are read as one thing, and an agency that
 * connected Google before TripAdvisor should not get a different page from one
 * who did it the other way round.
 *
 * `groupByPlatform` takes its rail order from this same sorted list, so the
 * rails and the scores above them cannot disagree.
 *
 * Anything not named here — a new integration, and Tripcart's own `platform`
 * reviews — keeps its payload position and follows the four.
 */
const PLATFORM_RANK: readonly string[] = [
  "tripadvisor",
  "google",
  "trustpilot",
  "facebook",
];

export function byPlatformRank(
  a: { platform: string },
  b: { platform: string },
): number {
  const rank = (p: string): number => {
    const i = PLATFORM_RANK.indexOf(p);
    return i === -1 ? PLATFORM_RANK.length : i;
  };
  return rank(a.platform) - rank(b.platform);
}

/** One connected platform's aggregate. Named for the whole row rather than for
 *  `count`, which it was called while the count was the only figure it carried
 *  and the only one the panel drew. */
export interface PlatformStats {
  platform: string;
  count: number;
  /** That platform's mean rating, already rounded to one decimal by the API so
   *  it cannot disagree with the overall score printed directly above it. */
  avgRating: number;
}

/** Columns for the platform grid from `lg`, by how many there are: two, three,
 *  or a 2×2 block for four. Only three differs from the phone's two, so it is
 *  the one `data-columns` value the module keys on.
 *
 *  `lg`, matching the panel's own chrome above. At `sm` the 640–1023px band
 *  got the DESKTOP column count on the PHONE's borderless surface, with the
 *  phone's gaps, because the card had not arrived yet — a layout nothing had
 *  decided, assembled from half of each. One breakpoint for the whole
 *  component. */
const PLATFORM_COLUMNS: Record<number, number> = { 2: 2, 3: 3, 4: 2 };

/** The platform's coloured disc. Exported so a review CARD draws the same mark
 *  as the summary panel above it — two copies is how one gained a platform the
 *  other had never heard of.
 *
 *  `size="chip"` is the guide's `.rv-fb .fb-chip .plat-m` override: the source
 *  filter shrinks the same disc to 17px/10px so it sits inside a 34px control
 *  without crowding its label. A bounded prop rather than a `className` escape
 *  hatch, so a third call site cannot quietly invent a fourth size. */
export function PlatformMark({
  platform,
  size = "default",
}: {
  platform: string;
  size?: "default" | "chip";
}) {
  const px = size === "chip" ? 17 : 20;
  const logo = <PlatformLogo platform={platform} size={px} />;
  // `PlatformLogo` answers null for anything it has no brand asset for —
  // Tripcart's own reviews, and any integration added after it. Those keep the
  // lettered disc, which is a legible fallback rather than a hole.
  if (logo !== null) {
    return (
      <span aria-hidden className={styles.logo}>
        {logo}
      </span>
    );
  }
  const mark = PLATFORM_MARKS[platform];
  return (
    <span
      aria-hidden
      className={styles.disc}
      data-size={size}
      data-tone={mark?.tone}
    >
      {mark?.initial ?? platform.slice(0, 1).toUpperCase()}
    </span>
  );
}

export function RatingSummary({
  average,
  total,
  platforms,
  t,
}: {
  average: number;
  total: number;
  platforms: PlatformStats[];
  t: Translator;
}) {
  const solo = platforms.length === 1;

  return (
    <div
      // Guide `.rvs` on the phone: NO box. The score sits on the page like
      // every other opening line in a section — a card around it made the
      // reviews section start with a card, inside a page whose sections are
      // deliberately card-free and separated by hairlines alone. Solo gives
      // the score the room and lets the single platform sit tight against the
      // right edge; otherwise the platforms take the rest.
      className={styles.summary}
      data-solo={solo ? "" : undefined}
    >
      {/* The dividing rule belongs to the multi-platform layout. With one
          platform there are not two groups to divide. */}
      <div className={styles.score}>
        <div className={styles.figure}>
          {average.toFixed(1)}
          {/* The muted tone, not a disabled one — the denominator is read. */}
          <span className={styles.outOf}>/5</span>
        </div>
        <div className={styles.stars}>
          <StarRow average={average} />
        </div>
        {/* NOT "for this tour", and not a promise about the list below.

            `reviews` has no experience column and `/api/v1/reviews` takes no
            tour filter — these are the AGENCY's reviews, shown on a tour page,
            so "for this tour" was never true of them.

            `total` is also the figure each platform reports for its whole
            listing (Google's 488), which is deliberately larger than the rows
            we store and can show. "Based on" is doing real work in this
            sentence: it names what the score above was computed from, not how
            many cards the reader can scroll to. */}
        <div className={styles.basis}>
          {t("Based on {n} reviews across all sources", { n: total })}
        </div>
      </div>

      {platforms.length > 0 ? (
        <div className={solo ? styles.platformsSolo : undefined}>
          <div className={styles.platformsLabel}>
            {/* With one source there is no "also". */}
            {solo ? t("Verified on") : t("Also rated on")}
          </div>
          <div
            // TWO across on the phone, the guide's `.rvs-grid`. One column of
            // four platforms is four lines of a number each, which is a lot of
            // height for a fact the score above already summarises. Solo
            // renders its one platform as a pill.
            className={styles.grid}
            data-columns={PLATFORM_COLUMNS[Math.min(platforms.length, 4)]}
          >
            {platforms.map((p) => (
              <span key={p.platform} className={styles.platform}>
                <PlatformMark platform={p.platform} />
                <span className={styles.platformName}>
                  {platformLabel(p.platform)}
                </span>
                <b className={styles.platformScore}>
                  {/* `toFixed(1)`, the same treatment the overall score above
                      gets — the guide prints "4.7" and "4.8", so a platform
                      averaging exactly 5 must read "5.0" beside them rather
                      than a bare "5". */}
                  {p.avgRating.toFixed(1)}
                </b>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
